import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, useContext } from 'react';

import AddToGroupModal from '@/components/AddToGroupModal';
import MemberEligibilityBadges from '@/components/MemberEligibilityBadges';
import type { Props } from '@/components/TreatmentGroupBookingView/types';
import TreatmentGroupDetailSections from '@/components/TreatmentGroupDetailSections';
import TreatmentGroupEnrollmentButton from '@/components/TreatmentGroupEnrollmentButton';
import AppContext from '@/contexts/appContext';
import useAddToGroupFlow from '@/hooks/useAddToGroupFlow';
import { useTreatmentGroupForPerson } from '@/hooks/useTreatmentGroups';
import { Alert, Pill, Spinner, Typography } from '@/ui';
import {
  hasPermission,
  TREATMENT_GROUP_MEMBERSHIP_ADD,
  TREATMENT_GROUP_MEMBERSHIP_CHANGE
} from '@/utils/userPermission';

const TreatmentGroupBookingView: FC<Props> = ({
  memberFirstName,
  onBack,
  personId,
  timeZone,
  treatmentGroupId
}) => {
  const { permissions } = useContext(AppContext);
  const canEnroll = hasPermission({
    permissions,
    requiredPermission: TREATMENT_GROUP_MEMBERSHIP_ADD
  });
  const canRemove = hasPermission({
    permissions,
    requiredPermission: TREATMENT_GROUP_MEMBERSHIP_CHANGE
  });

  const {
    data: groupResponse,
    isPending,
    isError
  } = useTreatmentGroupForPerson(treatmentGroupId, personId, timeZone);
  const group = groupResponse?.data;

  const membershipActions = useAddToGroupFlow(personId, timeZone);

  if (isPending) return <Spinner />;
  if (isError || !group) {
    return <Alert variant="error" message="Failed to load treatment group." />;
  }

  const { member_eligibility } = group;

  const handleEnroll = () => membershipActions.startEnroll(group);

  const handleManage = () => {
    const member = group.members.find((m) => m.person_id === personId);
    if (!member || !member_eligibility.membership_id) return;
    membershipActions.startManage(
      group,
      member_eligibility.membership_id,
      member.attending_group_session_ids,
      `Remove ${member.person_name} from "${group.name}"?`
    );
  };

  const canTakeEnrollmentAction = member_eligibility.is_enrolled ? canRemove : canEnroll;
  const enrollmentButton = canTakeEnrollmentAction ? (
    <TreatmentGroupEnrollmentButton
      isEnrolled={member_eligibility.is_enrolled}
      isPending={membershipActions.isPending}
      manageMode
      onEnroll={handleEnroll}
      onRemove={handleManage}
    />
  ) : null;

  return (
    <div className="space-y-6 rounded-lg bg-white p-6 dark:bg-uniform-gray-900">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1 text-sm text-uniform-gray-600 hover:text-uniform-gray-900 dark:text-uniform-gray-400 dark:hover:text-uniform-gray-100">
        <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
        Groups
      </button>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <Typography variant="heading">{group.name}</Typography>
          {member_eligibility.is_enrolled && (
            <Pill size="sm" variant="subtle" color="primary">
              ✓ Enrolled
            </Pill>
          )}
        </div>
        {enrollmentButton}
      </div>

      <MemberEligibilityBadges
        conflictOfInterestReason={member_eligibility.conflict_of_interest_reason}
        hasConflictOfInterest={member_eligibility.has_conflict_of_interest}
        ineligibilityReason={member_eligibility.ineligibility_reason}
        isCredentialed={member_eligibility.is_credentialed}
        isEligible={member_eligibility.is_eligible}
        isExisting={member_eligibility.is_enrolled}
      />

      <TreatmentGroupDetailSections
        group={group}
        memberActions={enrollmentButton}
        memberFirstName={memberFirstName}
        timezone={timeZone}
      />
      {membershipActions.modal && <AddToGroupModal {...membershipActions.modal} />}
    </div>
  );
};

export default TreatmentGroupBookingView;
